import { useState } from 'react'
import useSettingsStore from '../store/settingsStore'
import { useTerrariumStore, PLANT_TYPES, INSECT_TYPES } from '../store/terrariumStore'
import '../styles/SettingsPanel.css'

const SettingsPanel = ({ isAdmin = false, broadcasterLogin = null }) => {
  const {
    settings,
    isPanelOpen,
    togglePanel,
    closePanel,
    updateSetting,
    resetToDefaults,
    saveToApi,
    hasChanges,
    isLoading
  } = useSettingsStore()

  const plants = useTerrariumStore((state) => state.plants)
  const insects = useTerrariumStore((state) => state.insects)
  const clearAll = useTerrariumStore((state) => state.clearAll)
  const addPlant = useTerrariumStore((state) => state.addPlant)
  const addInsect = useTerrariumStore((state) => state.addInsect)
  const removePlant = useTerrariumStore((state) => state.removePlant)
  const removeInsect = useTerrariumStore((state) => state.removeInsect)
  const waterAllPlants = useTerrariumStore((state) => state.waterAllPlants)

  const [activeTab, setActiveTab] = useState('growth')
  const [saveStatus, setSaveStatus] = useState(null)

  // Admin form state
  const [adminPlantType, setAdminPlantType] = useState('flower')
  const [adminInsectType, setAdminInsectType] = useState('ladybug')
  const [adminUsername, setAdminUsername] = useState('')

  const handleSave = async () => {
    const success = await saveToApi()
    setSaveStatus(success ? 'saved' : 'error')
    setTimeout(() => setSaveStatus(null), 2000)
  }

  const handleAddPlant = async () => {
    if (!adminUsername.trim()) return
    const pseudoId = `manual_${adminUsername.trim()}_${Date.now()}`
    await addPlant(pseudoId, adminUsername.trim(), adminPlantType)
    setAdminUsername('')
  }

  const handleAddInsect = async () => {
    if (!adminUsername.trim()) return
    const pseudoId = `manual_${adminUsername.trim()}_${Date.now()}`
    await addInsect(pseudoId, adminUsername.trim(), adminInsectType)
    setAdminUsername('')
  }

  const handleClearAll = async () => {
    if (confirm('Remove ALL plants and insects? This cannot be undone!')) {
      await clearAll()
    }
  }

  const handleWaterAll = async () => {
    await waterAllPlants('admin', 'Admin')
  }

  const plantEntries = Object.entries(plants)
  const insectEntries = Object.entries(insects)

  if (!isPanelOpen) {
    return (
      <button
        className="settings-toggle-btn"
        onClick={togglePanel}
        title="Open Settings"
      >
        Settings
      </button>
    )
  }

  return (
    <div className="settings-overlay">
      <div className="settings-panel">
        <div className="settings-header">
          <h2>Terrarium Settings</h2>
          <button className="close-btn" onClick={closePanel}>x</button>
        </div>

        <div className="settings-tabs">
          <button className={`tab ${activeTab === 'growth' ? 'active' : ''}`} onClick={() => setActiveTab('growth')}>Growth</button>
          <button className={`tab ${activeTab === 'visual' ? 'active' : ''}`} onClick={() => setActiveTab('visual')}>Visual</button>
          <button className={`tab ${activeTab === 'display' ? 'active' : ''}`} onClick={() => setActiveTab('display')}>Display</button>
          <button className={`tab ${activeTab === 'permissions' ? 'active' : ''}`} onClick={() => setActiveTab('permissions')}>Permissions</button>
          <button className={`tab ${activeTab === 'insects' ? 'active' : ''}`} onClick={() => setActiveTab('insects')}>Insects</button>
          {isAdmin && (
            <button className={`tab ${activeTab === 'admin' ? 'active' : ''}`} onClick={() => setActiveTab('admin')}>Admin</button>
          )}
          {isAdmin && (broadcasterLogin === 'f00d4tehg0dz') && (
            <button className={`tab ${activeTab === 'bits' ? 'active' : ''}`} onClick={() => setActiveTab('bits')}>Bits</button>
          )}
          <button className={`tab ${activeTab === 'changelog' ? 'active' : ''}`} onClick={() => setActiveTab('changelog')}>Changelog</button>
          <button className={`tab ${activeTab === 'help' ? 'active' : ''}`} onClick={() => setActiveTab('help')}>Help</button>
        </div>

        <div className="settings-content">
          {/* Growth Tab */}
          {activeTab === 'growth' && (
            <div className="settings-section">
              <h3>Growth Settings</h3>
              <div className="setting-item">
                <label>Growth Rate <span className="setting-value">{settings.growthRate}% per cycle</span></label>
                <input type="range" min="1" max="100" step="5" value={settings.growthRate}
                  onChange={(e) => updateSetting('growthRate', parseInt(e.target.value))} />
              </div>
              <div className="setting-item">
                <label>Health Decay Rate <span className="setting-value">{settings.healthDecayRate}%/min (~{Math.round(100 / settings.healthDecayRate)} min to die)</span></label>
                <input type="range" min="0.1" max="5" step="0.1" value={settings.healthDecayRate}
                  onChange={(e) => updateSetting('healthDecayRate', parseFloat(e.target.value))} />
              </div>
              <div className="setting-item">
                <label>Water Cooldown <span className="setting-value">{settings.waterDurationMinutes} minutes</span></label>
                <input type="range" min="5" max="120" step="5" value={settings.waterDurationMinutes}
                  onChange={(e) => updateSetting('waterDurationMinutes', parseInt(e.target.value))} />
              </div>
              <div className="setting-item">
                <label>
                  <input type="checkbox" checked={settings.pauseDecayWhenOffline}
                    onChange={(e) => updateSetting('pauseDecayWhenOffline', e.target.checked)} />
                  Pause health decay when stream is offline
                </label>
              </div>

              <h3>Defaults</h3>
              <div className="setting-item">
                <label>Default Plant Type</label>
                <select value={settings.defaultPlantType} onChange={(e) => updateSetting('defaultPlantType', e.target.value)}>
                  {PLANT_TYPES.map((type) => (
                    <option key={type} value={type}>{type.charAt(0).toUpperCase() + type.slice(1)}</option>
                  ))}
                </select>
              </div>
              <div className="setting-item">
                <label>Default Insect Type</label>
                <select value={settings.defaultInsectType} onChange={(e) => updateSetting('defaultInsectType', e.target.value)}>
                  {INSECT_TYPES.map((type) => (
                    <option key={type} value={type}>{type.charAt(0).toUpperCase() + type.slice(1)}</option>
                  ))}
                </select>
              </div>

              <h3>Limits</h3>
              <div className="setting-item">
                <label>Max Plants <span className="setting-value">{settings.maxPlants}</span></label>
                <input type="range" min="5" max="100" step="5" value={settings.maxPlants}
                  onChange={(e) => updateSetting('maxPlants', parseInt(e.target.value))} />
              </div>
              <div className="setting-item">
                <label>Max Insects <span className="setting-value">{settings.maxInsects}</span></label>
                <input type="range" min="5" max="50" step="5" value={settings.maxInsects}
                  onChange={(e) => updateSetting('maxInsects', parseInt(e.target.value))} />
              </div>
            </div>
          )}

          {/* Visual Tab */}
          {activeTab === 'visual' && (
            <div className="settings-section">
              <h3>Visual Settings</h3>
              <div className="setting-item">
                <label>Plant Scale <span className="setting-value">{settings.plantScale.toFixed(1)}x</span></label>
                <input type="range" min="0.5" max="2.0" step="0.1" value={settings.plantScale}
                  onChange={(e) => updateSetting('plantScale', parseFloat(e.target.value))} />
              </div>
              <div className="setting-item checkbox">
                <label>
                  <input type="checkbox" checked={settings.showGrowthBars}
                    onChange={(e) => updateSetting('showGrowthBars', e.target.checked)} />
                  Show Status Bar
                </label>
              </div>
              <div className="setting-item checkbox">
                <label>
                  <input type="checkbox" checked={settings.showUsernames}
                    onChange={(e) => updateSetting('showUsernames', e.target.checked)} />
                  Show Usernames
                </label>
              </div>

              <h3>Animation Durations</h3>
              <div className="setting-item">
                <label>Water Animation <span className="setting-value">{settings.waterAnimationDuration}ms</span></label>
                <input type="range" min="500" max="5000" step="250" value={settings.waterAnimationDuration}
                  onChange={(e) => updateSetting('waterAnimationDuration', parseInt(e.target.value))} />
              </div>
            </div>
          )}

          {/* Display Tab */}
          {activeTab === 'display' && (
            <div className="settings-section">
              <h3>Scene Elements</h3>
              <p className="setting-hint" style={{ marginBottom: '12px' }}>Toggle terrarium elements on/off for a clean overlay.</p>
              <div className="setting-item checkbox">
                <label>
                  <input type="checkbox" checked={settings.showGround}
                    onChange={(e) => updateSetting('showGround', e.target.checked)} />
                  Show Ground / Soil
                </label>
              </div>
              <div className="setting-item checkbox">
                <label>
                  <input type="checkbox" checked={settings.showGlassFrame}
                    onChange={(e) => updateSetting('showGlassFrame', e.target.checked)} />
                  Show Glass Frame
                </label>
              </div>
              <div className="setting-item checkbox">
                <label>
                  <input type="checkbox" checked={settings.showWaterParticles}
                    onChange={(e) => updateSetting('showWaterParticles', e.target.checked)} />
                  Show Water Particles
                </label>
              </div>
              <div className="setting-item checkbox">
                <label>
                  <input type="checkbox" checked={settings.showNotifications}
                    onChange={(e) => updateSetting('showNotifications', e.target.checked)} />
                  Show Event Notifications
                </label>
              </div>
              <div className="setting-item checkbox">
                <label>
                  <input type="checkbox" checked={settings.showChatBubbles}
                    onChange={(e) => updateSetting('showChatBubbles', e.target.checked)} />
                  Show Chat Bubbles
                </label>
              </div>
            </div>
          )}

          {/* Permissions Tab */}
          {activeTab === 'permissions' && (
            <div className="settings-section">
              <h3>Viewer Permissions</h3>
              <div className="setting-item checkbox">
                <label>
                  <input type="checkbox" checked={settings.allowWatering}
                    onChange={(e) => updateSetting('allowWatering', e.target.checked)} />
                  Allow Viewers to Water Plants
                </label>
              </div>
              <div className="setting-item checkbox">
                <label>
                  <input type="checkbox" checked={settings.allowPollinating}
                    onChange={(e) => updateSetting('allowPollinating', e.target.checked)} />
                  Allow Pollinating
                </label>
              </div>
              <div className="setting-item checkbox">
                <label>
                  <input type="checkbox" checked={settings.allowVisiting}
                    onChange={(e) => updateSetting('allowVisiting', e.target.checked)} />
                  Allow Visiting
                </label>
              </div>

              <h3>Join Requirements</h3>
              <div className="setting-item checkbox">
                <label>
                  <input type="checkbox" checked={settings.requireSubscription}
                    onChange={(e) => updateSetting('requireSubscription', e.target.checked)} />
                  Require Subscription to Join
                </label>
              </div>
              {settings.requireSubscription && (
                <div className="setting-item">
                  <label>Required Tier</label>
                  <select value={settings.requiredTier} onChange={(e) => updateSetting('requiredTier', parseInt(e.target.value))}>
                    <option value={1000}>Tier 1</option>
                    <option value={2000}>Tier 2</option>
                    <option value={3000}>Tier 3</option>
                  </select>
                </div>
              )}

              <h3>Admin Permissions</h3>
              <div className="setting-item checkbox">
                <label>
                  <input type="checkbox" checked={settings.modCanUseAdminCommands}
                    onChange={(e) => updateSetting('modCanUseAdminCommands', e.target.checked)} />
                  Mods Can Use Admin Commands
                </label>
              </div>
              <div className="setting-item checkbox">
                <label>
                  <input type="checkbox" checked={settings.broadcasterOnlyAdmin}
                    onChange={(e) => updateSetting('broadcasterOnlyAdmin', e.target.checked)} />
                  Broadcaster-Only Admin
                </label>
              </div>
            </div>
          )}

          {/* Insects Tab */}
          {activeTab === 'insects' && (
            <div className="settings-section">
              <h3>Insect Roaming</h3>
              <div className="setting-item">
                <label>Roam Interval <span className="setting-value">{settings.roamInterval / 1000}s</span></label>
                <input type="range" min="2000" max="20000" step="1000" value={settings.roamInterval}
                  onChange={(e) => updateSetting('roamInterval', parseInt(e.target.value))} />
                <span className="setting-hint">How often insects pick new destinations</span>
              </div>
              <div className="setting-item">
                <label>Roam Speed <span className="setting-value">{settings.roamSpeed}</span></label>
                <input type="range" min="0.1" max="1.0" step="0.05" value={settings.roamSpeed}
                  onChange={(e) => updateSetting('roamSpeed', parseFloat(e.target.value))} />
              </div>
            </div>
          )}

          {/* Admin Tab */}
          {activeTab === 'admin' && isAdmin && (
            <div className="settings-section">
              <h3>Overview</h3>
              <div className="stats-grid">
                <div className="stat-box">
                  <span className="stat-number">{plantEntries.length}</span>
                  <span className="stat-label">Plants</span>
                </div>
                <div className="stat-box">
                  <span className="stat-number">{insectEntries.length}</span>
                  <span className="stat-label">Insects</span>
                </div>
              </div>

              <h3>Add Entity</h3>
              <div className="admin-actions">
                <div className="admin-action">
                  <div className="admin-form">
                    <input
                      type="text"
                      placeholder="Username"
                      value={adminUsername}
                      onChange={(e) => setAdminUsername(e.target.value)}
                      style={{ width: '100px' }}
                    />
                  </div>
                  <div className="admin-form" style={{ marginTop: '4px' }}>
                    <select value={adminPlantType} onChange={(e) => setAdminPlantType(e.target.value)}>
                      {PLANT_TYPES.map((type) => (
                        <option key={type} value={type}>{type.charAt(0).toUpperCase() + type.slice(1)}</option>
                      ))}
                    </select>
                    <button onClick={handleAddPlant} disabled={!adminUsername.trim()}>Add Plant</button>
                  </div>
                  <div className="admin-form" style={{ marginTop: '4px' }}>
                    <select value={adminInsectType} onChange={(e) => setAdminInsectType(e.target.value)}>
                      {INSECT_TYPES.map((type) => (
                        <option key={type} value={type}>{type.charAt(0).toUpperCase() + type.slice(1)}</option>
                      ))}
                    </select>
                    <button onClick={handleAddInsect} disabled={!adminUsername.trim()}>Add Insect</button>
                  </div>
                </div>
              </div>

              <h3>Quick Actions</h3>
              <div className="admin-actions">
                <div className="admin-action">
                  <button className="action-btn" onClick={handleWaterAll} disabled={plantEntries.length === 0}>
                    Water All Plants
                  </button>
                </div>
                <div className="admin-action">
                  <button className="danger-btn" onClick={handleClearAll} disabled={plantEntries.length === 0 && insectEntries.length === 0}>
                    Clear Terrarium
                  </button>
                </div>
              </div>

              <h3>Active Plants ({plantEntries.length})</h3>
              <div className="plant-list">
                {plantEntries.length === 0 ? (
                  <p className="empty-list">No plants</p>
                ) : (
                  plantEntries.map(([userId, plant]) => (
                    <div key={userId} className="plant-list-item">
                      <span className="plant-info">
                        <strong>{plant.username}</strong>
                        <span className="plant-type">{plant.plantType} | {plant.growthStage}% | HP: {plant.health}%</span>
                      </span>
                      <button className="remove-btn" onClick={() => removePlant(userId)}>x</button>
                    </div>
                  ))
                )}
              </div>

              <h3>Active Insects ({insectEntries.length})</h3>
              <div className="plant-list">
                {insectEntries.length === 0 ? (
                  <p className="empty-list">No insects</p>
                ) : (
                  insectEntries.map(([userId, insect]) => (
                    <div key={userId} className="plant-list-item">
                      <span className="plant-info">
                        <strong>{insect.username}</strong>
                        <span className="plant-type">{insect.insectType}</span>
                      </span>
                      <button className="remove-btn" onClick={() => removeInsect(userId)}>x</button>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Bits Tab */}
          {activeTab === 'bits' && isAdmin && broadcasterLogin === 'f00d4tehg0dz' && (
            <div className="settings-section">
              <h3>Bits Monetization</h3>
              <p style={{ fontSize: '0.8rem', color: '#adadb8', marginBottom: '10px' }}>
                In-Extension Bits: Twitch handles all Bits transactions.
              </p>

              <div className="setting-item checkbox">
                <label>
                  <input type="checkbox" checked={settings.enableBitsFeatures}
                    onChange={(e) => updateSetting('enableBitsFeatures', e.target.checked)} />
                  Enable Bits Features
                </label>
              </div>
              <div className="setting-item checkbox">
                <label>
                  <input type="checkbox" checked={settings.showBitsNotifications}
                    onChange={(e) => updateSetting('showBitsNotifications', e.target.checked)} />
                  Show Bits Notifications
                </label>
              </div>
              <div className="setting-item checkbox">
                <label>
                  <input type="checkbox" checked={settings.showBitsLeaderboard}
                    onChange={(e) => updateSetting('showBitsLeaderboard', e.target.checked)} />
                  Show Bits Leaderboard
                </label>
              </div>

              <h3>Tier 1 — Cosmetic Unlocks (Fixed Pricing)</h3>
              <div className="help-block">
                <div className="help-command"><code>50 bits</code><span>Golden Pot — shiny gold pot for plants</span></div>
                <div className="help-command"><code>100 bits</code><span>Neon/Crystal/Galaxy plant skins</span></div>
                <div className="help-command"><code>100 bits</code><span>Golden/Rainbow/Shadow insect skins</span></div>
                <div className="help-command"><code>250 bits</code><span>Custom Name Color</span></div>
                <div className="help-command"><code>500 bits</code><span>Legendary plants (Cherry Blossom, Crystal Tree, Venus Flytrap)</span></div>
                <div className="help-command"><code>500 bits</code><span>Legendary insects (Moth, Mantis, Scorpion)</span></div>
              </div>

              <h3>Tier 2 — Instant Effects</h3>
              <div className="help-block">
                <div className="help-command"><code>1 bit</code><span>Rain — waters all plants for 30s</span></div>
                <div className="help-command"><code>50 bits</code><span>Fireworks over terrarium</span></div>
                <div className="help-command"><code>100 bits</code><span>Mega Bloom — instant bloom</span></div>
                <div className="help-command"><code>200 bits</code><span>Butterfly Swarm (30s)</span></div>
                <div className="help-command"><code>500 bits</code><span>Earthquake — shakes terrarium</span></div>
              </div>

              <h3>Tier 3 — Persistent Perks (Cumulative)</h3>
              <div className="help-block">
                <div className="help-command"><code>500 total</code><span>Second Plant Slot</span></div>
                <div className="help-command"><code>1,000 total</code><span>Second Insect Slot</span></div>
                <div className="help-command"><code>2,500 total</code><span>VIP Badge</span></div>
                <div className="help-command"><code>5,000 total</code><span>Custom Plant Name</span></div>
              </div>

              <h3>Bits Commands</h3>
              <div className="help-block">
                <div className="help-command"><code>!shop [category]</code><span>Browse the bits shop (effects, cosmetics, perks)</span></div>
                <div className="help-command"><code>!mybits</code><span>Check your total bits used</span></div>
                <div className="help-command"><code>!mycosmetics</code><span>List unlocked cosmetics</span></div>
                <div className="help-command"><code>!equip [name]</code><span>Equip skin or legendary type</span></div>
                <div className="help-command"><code>!unequip</code><span>Remove current cosmetic</span></div>
                <div className="help-command"><code>!rename [name]</code><span>Rename your plant (5,000 bits perk)</span></div>
              </div>
            </div>
          )}

          {/* Changelog Tab */}
          {activeTab === 'changelog' && (
            <div className="settings-section help-section">
              <h3>Changelog</h3>

              <div className="changelog-version">
                <h4>v1.5.0 <span className="changelog-date">March 2026</span></h4>
                <div className="help-block">
                  <p><strong>New Insects:</strong> Worm, Moth (legendary), Mantis (legendary), Scorpion (legendary)</p>
                  <p><strong>New Plants:</strong> Cherry Blossom, Crystal Tree, Venus Flytrap (all legendary)</p>
                  <p><strong>Health:</strong> Plants pause decay when stream is offline. Fractional decay rates supported.</p>
                  <p><strong>Commands:</strong> Added !wave, !sing, !nuzzle, !pet</p>
                  <p><strong>UI:</strong> Changelog tab, updated Help tab, fixed tab overflow, auto-dismiss notifications</p>
                  <p><strong>Extension:</strong> Twitch panel extension with shop UI</p>
                </div>
              </div>

              <div className="changelog-version">
                <h4>v1.4.0 <span className="changelog-date">February 2026</span></h4>
                <div className="help-block">
                  <p><strong>Multi-Entity:</strong> Viewers can join as plants or insects</p>
                  <p><strong>Insects:</strong> 9 types — flying (butterfly, bee, firefly, dragonfly) and crawling (ladybug, caterpillar, beetle, snail)</p>
                  <p><strong>Commands:</strong> !pollinate, !visit, !dance, !land, !fly</p>
                  <p><strong>Settings:</strong> Insect roaming controls, display toggles</p>
                </div>
              </div>

              <div className="changelog-version">
                <h4>v1.3.0 <span className="changelog-date">January 2026</span></h4>
                <div className="help-block">
                  <p><strong>Settings Panel:</strong> Full settings with Growth, Visual, Display, Permissions, Insects, Admin, Help tabs</p>
                  <p><strong>Permissions:</strong> Subscription gating (Tier 1/2/3), mod controls</p>
                </div>
              </div>

              <div className="changelog-version">
                <h4>v1.2.0 <span className="changelog-date">December 2025</span></h4>
                <div className="help-block">
                  <p><strong>Chat Bubbles:</strong> Messages appear above entities</p>
                  <p><strong>Notifications:</strong> Event popups for joins, waters, pollinations</p>
                </div>
              </div>

              <div className="changelog-version">
                <h4>v1.1.0 <span className="changelog-date">November 2025</span></h4>
                <div className="help-block">
                  <p><strong>Plants:</strong> Expanded to 12 types with unique CSS art</p>
                  <p><strong>Commands:</strong> !bloom, !harvest, !shake</p>
                </div>
              </div>

              <div className="changelog-version">
                <h4>v1.0.0 <span className="changelog-date">October 2025</span></h4>
                <div className="help-block">
                  <p><strong>Initial Release:</strong> Chat-powered terrarium overlay with plants, watering, OBS integration, and PostgreSQL persistence</p>
                </div>
              </div>
            </div>
          )}

          {/* Help Tab */}
          {activeTab === 'help' && (
            <div className="settings-section help-section">
              <h3>Welcome to Terrarium!</h3>
              <div className="help-block">
                <p>
                  Each viewer gets their own plant or insect in a shared terrarium!
                  Plants grow over time, insects roam around, and they can interact.
                </p>
              </div>

              <h3>Join Commands</h3>
              <div className="help-block">
                <div className="help-command"><code>!join plant [type]</code><span>Join as a plant</span></div>
                <div className="help-command"><code>!join insect [type]</code><span>Join as an insect</span></div>
                <div className="help-command"><code>!join [type]</code><span>Smart join — auto-detects type</span></div>
                <div className="help-command"><code>!leave</code><span>Remove yourself from the terrarium</span></div>
              </div>

              <h3>Plant Commands</h3>
              <div className="help-block">
                <div className="help-command"><code>!water [@user]</code><span>Water your plant or someone else's</span></div>
                <div className="help-command"><code>!bloom</code><span>Bloom your plant at 100% growth</span></div>
                <div className="help-command"><code>!harvest</code><span>Harvest your mature plant</span></div>
                <div className="help-command"><code>!shake</code><span>Shake your plant</span></div>
                <div className="help-command"><code>!wave</code><span>Wave at everyone</span></div>
              </div>

              <h3>Insect Commands</h3>
              <div className="help-block">
                <div className="help-command"><code>!fly</code><span>Fly to a random spot</span></div>
                <div className="help-command"><code>!land [@user]</code><span>Land on a plant</span></div>
                <div className="help-command"><code>!pollinate @user</code><span>Help a plant grow</span></div>
                <div className="help-command"><code>!visit @user</code><span>Visit another entity</span></div>
                <div className="help-command"><code>!dance</code><span>Make your insect dance</span></div>
                <div className="help-command"><code>!pet [@user]</code><span>Pet an insect</span></div>
                <div className="help-command"><code>!sing</code><span>Make your insect sing</span></div>
                <div className="help-command"><code>!nuzzle @user</code><span>Nuzzle another entity</span></div>
                <div className="help-command"><code>!sting @user</code><span>Sting someone (bees only)</span></div>
                <div className="help-command"><code>!birthdayhat @user</code><span>Give a birthday hat (24h)</span></div>
                <div className="help-command"><code>!tophat @user</code><span>Give a top hat (24h)</span></div>
                <div className="help-command"><code>!crown @user</code><span>Give a crown (24h)</span></div>
                <div className="help-command"><code>!santahat @user</code><span>Give a santa hat (24h)</span></div>
                <div className="help-command"><code>!cowboyhat @user</code><span>Give a cowboy hat (24h)</span></div>
                <div className="help-command"><code>!wizardhat @user</code><span>Give a wizard hat (24h)</span></div>
                <div className="help-command"><code>!removehat</code><span>Remove your hat</span></div>
                <div className="help-command"><code>!wave</code><span>Wave at everyone</span></div>
              </div>

              <h3>Info Commands</h3>
              <div className="help-block">
                <div className="help-command"><code>!plants</code><span>List plant types</span></div>
                <div className="help-command"><code>!insects</code><span>List insect types</span></div>
                <div className="help-command"><code>!help</code><span>Show all commands in chat</span></div>
                <div className="help-command"><code>!terrarium</code><span>What is Stream Terrarium?</span></div>
                <div className="help-command"><code>!commands</code><span>Full command list</span></div>
              </div>

              <h3>Bits Commands</h3>
              <div className="help-block">
                <div className="help-command"><code>!shop [category]</code><span>Browse the bits shop (effects, cosmetics, perks)</span></div>
                <div className="help-command"><code>!mybits</code><span>Check your total bits used</span></div>
                <div className="help-command"><code>!mycosmetics</code><span>List your unlocked cosmetics</span></div>
                <div className="help-command"><code>!equip [name]</code><span>Equip skin or legendary type</span></div>
                <div className="help-command"><code>!unequip</code><span>Remove current cosmetic</span></div>
                <div className="help-command"><code>!rename [name]</code><span>Rename your plant (5,000 bits perk)</span></div>
              </div>

              <h3>Admin Commands</h3>
              <div className="help-block">
                <div className="help-command"><code>!addplant @user [type]</code><span>Add a plant for a user</span></div>
                <div className="help-command"><code>!addinsect @user [type]</code><span>Add an insect for a user</span></div>
                <div className="help-command"><code>!removeentity @user</code><span>Remove a user's entity</span></div>
                <div className="help-command"><code>!removeplant @user</code><span>Remove a user's plant</span></div>
                <div className="help-command"><code>!removeinsect @user</code><span>Remove a user's insect</span></div>
                <div className="help-command"><code>!waterall</code><span>Water all plants</span></div>
                <div className="help-command"><code>!clearterrarium</code><span>Clear everything</span></div>
              </div>

              <h3>Available Types</h3>
              <div className="help-block">
                <p><strong>Plants:</strong> {PLANT_TYPES.join(', ')}</p>
                <p><strong>Insects:</strong> {INSECT_TYPES.join(', ')}</p>
              </div>
            </div>
          )}
        </div>

        <div className="settings-footer">
          <button className="reset-btn" onClick={resetToDefaults}>Reset to Defaults</button>
          <div className="save-area">
            {saveStatus === 'saved' && <span className="save-success">Saved!</span>}
            {saveStatus === 'error' && <span className="save-error">Error saving</span>}
            <button className="save-btn" onClick={handleSave} disabled={!hasChanges || isLoading}>
              {isLoading ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SettingsPanel